import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { X } from 'lucide-react'

/**
 * Fullscreen image lightbox.
 *
 * Two usage modes:
 *
 * 1. **Self-contained** — renders a clickable thumbnail that opens the lightbox:
 *    ```tsx
 *    <ImageLightbox src="/img.png" alt="Screenshot" />
 *    ```
 *
 * 2. **Controlled** — parent manages open/close (e.g. inside another dialog):
 *    ```tsx
 *    <ImageLightbox src={img.src} alt={img.alt} open={!!img} onClose={() => setImg(null)} />
 *    ```
 */
export function ImageLightbox({
  src,
  alt,
  open,
  onClose,
  thumbnailClassName,
}: {
  src: string
  alt: string
  /** Controlled mode: whether the lightbox is open */
  open?: boolean
  /** Controlled mode: called when the lightbox should close */
  onClose?: () => void
  /** Extra classes for the thumbnail image (self-contained mode only) */
  thumbnailClassName?: string
}) {
  const controlled = open !== undefined

  const overlay = (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 bg-black/80 z-[100] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
      <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95">
        <Dialog.Close asChild>
          <button
            className="fixed top-6 right-6 p-2.5 rounded-lg bg-black/50 text-white hover:bg-black/70 transition-colors z-[101]"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </Dialog.Close>
        <Dialog.Close asChild>
          <button className="cursor-zoom-out" aria-label="Close fullscreen image">
            <img
              src={src}
              alt={alt}
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            />
          </button>
        </Dialog.Close>
        <VisuallyHidden>
          <Dialog.Title>{alt}</Dialog.Title>
          <Dialog.Description>Full-screen image preview. Press Escape to close.</Dialog.Description>
        </VisuallyHidden>
      </Dialog.Content>
    </Dialog.Portal>
  )

  if (controlled) {
    return (
      <Dialog.Root open={open} onOpenChange={(o) => { if (!o && onClose) onClose() }}>
        {overlay}
      </Dialog.Root>
    )
  }

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <button className="block mt-3 cursor-zoom-in group">
          <img
            src={src}
            alt={alt}
            className={thumbnailClassName ?? 'rounded-lg max-h-56 object-contain border border-border group-hover:border-primary/40 transition-colors'}
            loading="lazy"
          />
        </button>
      </Dialog.Trigger>
      {overlay}
    </Dialog.Root>
  )
}
